import { createRouter, createMemoryHistory, createWebHistory } from "vue-router";
import MainPage from "@/views/MainPage.vue";
import { useCartStore } from "@/stores/cart.store";
import { useFastDeliveryCartStore } from "@/stores/fastDeliveryCart.store";
import { applyRouteSeo, ROUTE_DESCRIPTIONS } from "@/helpers/seo.js";
import { trackPageView } from "@/helpers/analytics.js";

export const mainRoutes = [
    "/",
    "/blog",
    "/recipes",
    "/fast-delivery",
    "/cart",
    "/my-orders",
    "/cart/confirmation",
    "/fast/cart/confirmation",
    "/user-profile-page",
    "/registerPage",
    "/login",
    "/verifyPage",
    "/about",
    "/privacy-policy",
    "/return-refund-policy",
    "/gest-profile-page",
    "/order-message",
];

export const loggedInMainRoutes = [
    "/",
    "/blog",
    "/recipes",
    "/fast-delivery",
    "/cart",
    "/my-orders",
    "/cart/confirmation",
    "/fast/cart/confirmation",
    "/user-profile-page",
    "/about",
    "/privacy-policy",
    "/return-refund-policy",
    "/order-message",
];

export const unLoggedInMainRoutes = [
    "/",
    "/blog",
    "/recipes",
    "/fast-delivery",
    "/registerPage",
    "/login",
    "/verifyPage",
    "/gest-profile-page",
    "/cart",
];

/** `/category/:id` — same UX tier as home for back/history. */
const CATEGORY_PATH_RE = /^\/category\/\d+\/?$/;

export function isMainSurfacePath(path) {
    const p = String(path || "").split("?")[0].split("#")[0];
    return mainRoutes.includes(p) || CATEGORY_PATH_RE.test(p);
}

function isAllowedBackPath(path, routeList) {
    const p = String(path || "").split("?")[0].split("#")[0];
    return routeList.includes(p) || CATEGORY_PATH_RE.test(p);
}

function buildRoutes(ssr) {
    return [
        {
            path: "/",
            name: "home",
            component: MainPage,
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                fastDelivery: false,
                title: "الرئيسية",
                description:
                    "تسوق منتجات طازجة ولحوم مختارة عبر متجر طازج الإلكتروني، عروض وتوصيل منظم للمناطق المختارة في السعودية.",
            },
        },
        {
            path: "/product/:id",
            name: "product-detail",
            component: () => import("@/views/ProductPage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                fastDelivery: false,
                title: "منتج",
            },
        },
        {
            path: "/category/:id",
            name: "category-detail",
            component: MainPage,
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                fastDelivery: false,
                title: "تصنيف",
            },
        },
        {
            path: "/blog",
            name: "blog-list",
            component: () => import("@/views/BlogListPage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                fastDelivery: false,
                title: "المدونة",
                description: ROUTE_DESCRIPTIONS["blog-list"],
            },
        },
        {
            path: "/blog/:slug",
            name: "blog-article",
            component: () => import("@/views/BlogArticlePage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                fastDelivery: false,
                title: "مقال",
                description: ROUTE_DESCRIPTIONS["blog-article"],
            },
        },
        {
            path: "/recipes",
            name: "recipes-list",
            component: () => import("@/views/RecipesListPage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                fastDelivery: false,
                title: "الوصفات",
                description: ROUTE_DESCRIPTIONS["recipes-list"],
            },
        },
        {
            path: "/recipes/:slug",
            name: "recipe-detail",
            component: () => import("@/views/RecipeDetailPage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                fastDelivery: false,
                title: "وصفة",
                description: ROUTE_DESCRIPTIONS["recipe-detail"],
            },
        },
        {
            path: "/fast-delivery",
            name: "fast-delivery-home",
            component: () => import("@/views/FastDeliveryPage.vue"),
            meta: {
                hideTabBar: false,
                fastDelivery: true,
                requiresAuth: false,
                title: "التوصيل السريع",
            },
        },
        {
            path: "/fast-delivery/tracking-order",
            name: "fast-delivery-tracking-order",
            component: () => import("@/views/FastDeliveryTrackingOrderPage.vue"),
            meta: {
                hideTabBar: false,
                fastDelivery: true,
                requiresAuth: false,
                title: "متابعة الطلب",
            },
        },
        {
            path: "/.well-known/apple-developer-merchantid-domain-association.txt",
            name: "merchant",
            beforeEnter: (to, from, next) => {
                if (ssr) {
                    next(false);
                    return;
                }
                window.location.href = "/src/assets/apple-developer-merchantid-domain-association.txt";
            },
        },
        {
            path: "/cart",
            name: "cart",
            component: () => import("@/views/CartPage.vue"),
            meta: {
                hideTabBar: true,
                requiresAuth: false,
                fastDelivery: false,
                title: "السلة",
            },
        },
        {
            path: "/user-profile-page",
            name: "user-profile-page",
            component: () => import("@/views/UserProfilePage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: true,
                fastDelivery: false,
                title: "الملف الشخصي ",
            },
        },
        {
            path: "/gest-profile-page",
            name: "gest-profile-page",
            component: () => import("@/views/GestProfilePage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                mustBeGuest: true,
                fastDelivery: false,
                title: "صفحة الزائر",
            },
        },
        {
            path: "/my-orders",
            name: "my-orders",
            component: () => import("@/views/MyOrderPage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: true,
                fastDelivery: false,
                title: "الطلبات",
            },
        },
        {
            path: "/order-message",
            name: "order-message",
            component: () => import("@/views/OrderMessagePage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: true,
                fastDelivery: false,
                title: "رد على الطلب",
            },
        },
        {
            path: "/tamara",
            name: "tamara-callback",
            component: () => import("@/views/OrderMessagePage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: true,
                fastDelivery: false,
                title: "رد على الطلب",
            },
        },
        {
            path: "/tamara/:status",
            name: "tamara-status-callback",
            component: () => import("@/views/OrderMessagePage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: true,
                fastDelivery: false,
                title: "رد على الطلب",
            },
        },
        {
            path: "/login",
            name: "login",
            component: () => import("@/views/loginPage.vue"),
            meta: {
                hideTabBar: true,
                requiresAuth: false,
                mustBeGuest: true,
                fastDelivery: false,
                title: "تسجيل الدخول",
            },
        },
        {
            path: "/registerPage",
            name: "registerPage",
            component: () => import("@/views/registerPage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                mustBeGuest: true,
                fastDelivery: false,
                title: "حساب جديد",
            },
        },
        {
            path: "/verifyPage",
            name: "verifyPage",
            component: () => import("@/views/verifyPage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                mustBeGuest: false,
                fastDelivery: false,
                title: "التحقق من الجوال",
            },
        },
        {
            path: "/addresses",
            name: "addresses",
            component: () => import("@/views/Addresses.vue"),
            meta: {
                hideTabBar: true,
                requiresAuth: true,
                mustBeGuest: false,
                fastDelivery: false,
                title: "العناوين",
            },
        },
        {
            path: "/fast-delivery/address",
            name: "fast-delivery-address",
            component: () => import("@/views/Addresses.vue"),
            meta: {
                hideTabBar: true,
                requiresAuth: true,
                fastDelivery: true,
                mustBeGuest: false,
                title: "العناوين",
            },
        },
        {
            path: "/map",
            name: "map",
            component: () => import("@/views/Map.vue"),
            meta: {
                hideTabBar: true,
                requiresAuth: true,
                mustBeGuest: false,
                fastDelivery: false,
                title: "الخارطة",
            },
        },
        {
            path: "/cart/confirmation",
            name: "cart-confirmation",
            component: () => import("@/views/CartConfirmationPage.vue"),
            meta: {
                requiresAuth: true,
                hideTabBar: true,
                fastDelivery: false,
                title: "تأكيد الطلب",
            },
        },
        {
            path: "/fast/cart/confirmation",
            name: "fast-cart-confirmation",
            component: () => import("@/views/FastDeliveryCartConfirmationPage.vue"),
            meta: {
                requiresAuth: true,
                fastDelivery: true,
                hideTabBar: true,
                title: "تأكيد الطلب",
            },
        },
        {
            path: "/about",
            name: "about",
            component: () => import("@/views/AboutCompanyPage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                fastDelivery: false,
                title: "عن الشركة",
            },
        },
        {
            path: "/privacy-policy",
            name: "privacy-policy",
            component: () => import("@/views/PrivacyPolicyPage.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: false,
                fastDelivery: false,
                title: "سياسات الخصوصية",
            },
        },
        {
            path: "/return-refund-policy",
            name: "return-refund-policy",
            component: () => import("@/views/ReturnRefundPolicyPage.vue"),
            meta: {
                hideTabBar: true,
                requiresAuth: false,
                fastDelivery: false,
                title: "سياسة الإرجاع واسترداد الأموال",
            },
        },
        {
            path: "/online-payment",
            name: "online-payment",
            component: () => import("@/views/HyperPayForm.vue"),
            meta: {
                hideTabBar: false,
                requiresAuth: true,
                fastDelivery: false,
                title: "الدفع الالكتروني",
            },
        },
    ];
}

/**
 * @param {boolean} ssr - use memory history and skip client-only guards when true
 */
export function createAppRouter(ssr) {
    const base = import.meta.env.BASE_URL || "/";
    const router = createRouter({
        history: ssr ? createMemoryHistory(base) : createWebHistory(base),
        routes: buildRoutes(ssr),
        scrollBehavior(to, from, savedPosition) {
            if (to.name === "category-detail" || from.name === "category-detail") {
                return false;
            }
            if (savedPosition) {
                return savedPosition;
            }
            return { top: 0 };
        },
    });

    router.beforeEach((to, from, next) => {
        if (ssr) {
            const token = null;
            const cartStore = useCartStore();
            const fastCartStore = useFastDeliveryCartStore();
            const currentHour = new Date().getUTCHours() + 3;
            if (to.meta.fastDelivery && (currentHour < 10 || currentHour > 16)) {
                next("/");
                return;
            }
            if (to.path === "/cart/confirmation" && cartStore.products.length === 0) {
                next("/");
                return;
            }
            if (to.path === "/fast/cart/confirmation" && fastCartStore.products.length === 0) {
                next("/");
                return;
            }
            if (to.meta.requiresAuth && !token) {
                next("/login");
                return;
            }
            if (to.meta.mustBeGuest && token) {
                next("/");
                return;
            }
            next();
            return;
        }

        const token = localStorage.getItem("token");

        if (isMainSurfacePath(to.path)) {
            let history = JSON.parse(localStorage.getItem("mainRouteHistory") || "[]");
            if (history.length === 0 || history[history.length - 1] !== to.path) {
                history.push(to.path);
                if (history.length > 10) history = history.slice(-10);
                localStorage.setItem("mainRouteHistory", JSON.stringify(history));
            }
        }

        if (isMainSurfacePath(to.path)) {
            localStorage.setItem("lastMainRoute", to.path);
        }

        const currentHour = new Date().getUTCHours() + 3;
        if (to.meta.fastDelivery && (currentHour < 10 || currentHour > 16)) {
            next("/");
            return;
        }

        const cartStore = useCartStore();
        const fastCartStore = useFastDeliveryCartStore();

        if (to.path === "/cart/confirmation" && cartStore.products.length === 0) {
            next("/");
            return;
        }

        if (to.path === "/fast/cart/confirmation" && fastCartStore.products.length === 0) {
            next("/");
            return;
        }

        if (to.meta.requiresAuth && !token) {
            next("/login");
            return;
        }

        if (to.meta.mustBeGuest && token) {
            next("/");
            return;
        }

        next();
    });

    router.afterEach((to) => {
        if (!ssr) {
            applyRouteSeo(to);
            trackPageView(to);
        }
    });

    router.goBack = () => {
        if (typeof localStorage === "undefined") {
            router.push("/");
            return;
        }
        const token = localStorage.getItem("token");
        const routeList = token ? loggedInMainRoutes : unLoggedInMainRoutes;
        let history = JSON.parse(localStorage.getItem("mainRouteHistory") || "[]");

        if (history.length > 0 && history[history.length - 1] === router.currentRoute.value.path) {
            history.pop();
        }
        let previousMainRoute = "/";
        while (history.length > 0) {
            const candidate = history.pop();
            if (isAllowedBackPath(candidate, routeList)) {
                previousMainRoute = candidate;
                break;
            }
        }

        localStorage.setItem("mainRouteHistory", JSON.stringify(history));
        router.push(previousMainRoute);
    };

    return router;
}
